import React, { Dispatch, useEffect } from "react";
import { useImmer } from "use-immer";
import { z } from "zod/v4";

import {
  IgButton,
  IgSelect,
  IgTextField,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  OrgShopIdDto,
  StripeApiCreateCustomerDtoSchema,
  StripeApiCreateCustomerResponseBodyType,
  StripeApiCustomerDto,
} from "@intelligems/ig-types";
import { Grid, MenuItem, useTheme } from "@mui/material";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetOrgList } from "@src/api/organizations-list/get-organization-list";
import { usePostStripeApi } from "@src/api/stripe/stripe-api";
import { CreateSubscriptionReducerAction } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/subscriptionReducer";

interface CreateCustomerProps {
  orgs: OrgShopIdDto[];
  customer: StripeApiCustomerDto | null | undefined;
  setCustomer: (customer: StripeApiCustomerDto) => void;
  dispatchPayload?: Dispatch<CreateSubscriptionReducerAction>;
}

const initCustomer = {
  id: "",
  name: "",
  email: "",
  billing: false,
};

export const CreateCustomer = ({
  orgs,
  customer,
  dispatchPayload,
  setCustomer,
}: CreateCustomerProps) => {
  const theme = useTheme();
  const createCustomer =
    usePostStripeApi<StripeApiCreateCustomerResponseBodyType>();
  const [error, setError] = useImmer<z.ZodError<any> | null>(null);
  const [_customer, _setCustomer] =
    useImmer<StripeApiCustomerDto>(initCustomer);
  const [selectedOrgId, setSelectedOrgId] = useImmer<string>("");

  const orgList = useGetOrgList();

  const getOrgName = (orgId: string): string => {
    if (orgList.isSuccess) {
      const org = orgList.data.orgs.find((o) => o.id === orgId);
      return org?.name || "";
    }
    return "";
  };

  useEffect(() => {
    if (orgs.length === 1) {
      setSelectedOrgId(orgs[0].id);
    }
  }, [orgs]);

  const onCreate = () => {
    const parsedCustomer =
      StripeApiCreateCustomerDtoSchema.safeParse(_customer);
    if (parsedCustomer.success) {
      createCustomer.mutate(
        {
          orgs,
          action: "createCustomer",
          customer: _customer,
        },
        {
          onSuccess: (data) => {
            setCustomer(data.customer);
            _setCustomer(initCustomer);
          },
        }
      );
    } else {
      setError(parsedCustomer.error);
    }
  };

  const createShopifyCustomer = () => {
    if (!dispatchPayload) {
      return;
    }
    const selectedOrg = orgs.find((org) => org.id === selectedOrgId) || orgs[0];

    _setCustomer((draft) => {
      draft.name = selectedOrg.shopId;
      draft.email = "no-bill@intelligems.io";
      return draft;
    });

    const customerData = {
      ...initCustomer,
      name: selectedOrg.shopId,
      email: "no-bill@intelligems.io",
    };

    const parsedCustomer =
      StripeApiCreateCustomerDtoSchema.safeParse(customerData);
    if (parsedCustomer.success) {
      createCustomer.mutate(
        {
          orgs,
          action: "createCustomer",
          customer: customerData,
        },
        {
          onSuccess: (data) => {
            setCustomer(data.customer);
            _setCustomer(initCustomer);
            dispatchPayload({ type: "setBillingProvider", value: "shopify" });
          },
        }
      );
    } else {
      setError(parsedCustomer.error);
    }
  };

  return (
    <Grid container item direction={"column"} rowSpacing={2}>
      <Grid item>
        <IgTypography>Create New Customer</IgTypography>
      </Grid>
      {orgs.length > 1 && (
        <Grid item xs={12}>
          <IgSelect
            fullWidth
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Organization
            </MenuItem>
            {orgs.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {getOrgName(org.id)} ({org.shopId})
              </MenuItem>
            ))}
          </IgSelect>
        </Grid>
      )}
      <Grid item container direction={"column"}>
        <Grid item>
          <IgTypography color={"tertiary"} variant={"body3"}>
            Name
          </IgTypography>
        </Grid>

        <Grid item>
          <IgTextField
            fullWidth={true}
            value={_customer.name}
            onChange={(e) => {
              _setCustomer((draft) => {
                draft.name = e.target.value;
                return draft;
              });
            }}
          />
        </Grid>
      </Grid>
      <Grid item container direction={"column"}>
        <Grid item>
          <IgTypography color={"tertiary"} variant={"body3"}>
            Email
          </IgTypography>
        </Grid>

        <Grid item>
          <IgTextField
            fullWidth={true}
            value={_customer.email}
            onChange={(e) => {
              setError(null);
              _setCustomer((draft) => {
                draft.email = e.target.value === "" ? null : e.target.value;
                return draft;
              });
            }}
          />
        </Grid>
      </Grid>
      {error ? (
        <Grid>
          <IgTypography
            sx={{ color: theme.palette.text.tonal.error }}
            variant={"body2"}
          >
            {error.issues[0].message}
          </IgTypography>
        </Grid>
      ) : null}
      <Grid container item spacing={2}>
        {dispatchPayload ? (
          <Grid item>
            <IgButton
              variant="contained"
              onClick={createShopifyCustomer}
              disabled={orgs.length > 1 && !selectedOrgId}
            >
              Create Shopify Customer
            </IgButton>
          </Grid>
        ) : null}
        <Grid item>
          <IgButton
            variant="outlined"
            onClick={onCreate}
            disabled={!_customer.name || !_customer.email}
          >
            Create Customer
          </IgButton>
        </Grid>
      </Grid>
    </Grid>
  );
};
